/* eslint max-lines: ["error", { "max": 1000 }] */
import { MIME_TYPES } from '@mantine/dropzone';
import { IconUpload, IconWand, IconX } from '@tabler/icons-react';
import {
  AllowanceModelDto,
  OEPPaymentMethod,
  OpenEnrollmentPeriodUpdateErrorDto,
} from '@zorro/clients';
import {
  APP_ROUTES,
  extractClassDefinitionsFromCsv,
} from '@zorro/common-utils';
import { Alert } from '@zorro/design-system/v2/Alert';
import { Badge } from '@zorro/design-system/v2/Badge';
import { Button } from '@zorro/design-system/v2/Button';
import { Checkbox } from '@zorro/design-system/v2/Checkbox';
import { Link } from '@zorro/design-system/v2/Link';
import { List } from '@zorro/design-system/v2/List';
import { Loader } from '@zorro/design-system/v2/Loader';
import { Radio, RadioCard, RadioGroup } from '@zorro/design-system/v2/Radio';
import { Switch } from '@zorro/design-system/v2/Switch';
import { Title } from '@zorro/design-system/v2/Title';
import {
  IconZorroPaymentCard,
  IconZorroPaymentSelfPay,
  TableIcon,
} from '@zorro/shared/assets';
import {
  EN_LOCALE_DATE_FORMAT,
  getToday,
  isBusinessDay,
} from '@zorro/shared/formatters';
import { oepPaymentMethodLabelConfig } from '@zorro/types';
import {
  Box,
  CurrencyInput,
  DateInput,
  DateRangeInput,
  FormErrorMessage,
  Grid,
  Group,
  LabelWithTooltip,
  MonthPickerInput,
  Stack,
  Text,
} from '@zorro/web/design-system';
import { useEmployerFeatureFlag } from '@zorro/web/ui-utils';
import { useState } from 'react';
import { Control, Controller, UseFormReturn } from 'react-hook-form';

import { FileUploader } from '../FileUploader';
import { AllowanceModelManagement } from './AllowanceModelManagement';
import { AllowanceModelSourceFields } from './AllowanceModelSourceFields';
import {
  CoverageScope,
  PlanYearSetupFormFields,
  getCoverageEndMonthBounds,
  processClassDefinitions,
} from './PlanYearSetupUtils';

const CLASS_DEFINITIONS_GUIDE_URL =
  'https://docs.google.com/document/d/13EQhbstL6DnC2RYbpIqyrR_h_jKXol23AAy4eTrBcdQ/edit?tab=t.0#heading=h.omlkn2dj16j7';

type Props = {
  planYearSetupForm: UseFormReturn<PlanYearSetupFormFields>;
  isFinalizationMode?: boolean;
  isAllowanceModelRequired?: boolean;
  employerId: string;
  planYearUpdateErrors: OpenEnrollmentPeriodUpdateErrorDto[];
  allowanceModelSourceNote?: string | null;
  allowanceModelSourceLabel?: string | null;
  existingAllowanceModel?: AllowanceModelDto | null;
};

enum AllowanceModelUploadMode {
  FROM_QUOTE = 'FROM_QUOTE',
  MANUAL_UPLOAD = 'MANUAL_UPLOAD',
}

export const PlanYearSetupFormInputs = ({
  isFinalizationMode = false,
  isAllowanceModelRequired = true,
  planYearSetupForm,
  employerId,
  planYearUpdateErrors,
  allowanceModelSourceNote,
  allowanceModelSourceLabel,
  existingAllowanceModel,
}: Props) => {
  'use no memo';
  const { formState, control, setValue, watch } = planYearSetupForm;
  const { errors } = formState;

  const allowanceModel = watch('allowanceModel');
  const [uploadMode, setUploadMode] = useState(
    AllowanceModelUploadMode.FROM_QUOTE
  );
  const isStoredModelShown =
    Boolean(allowanceModel) && !(allowanceModel instanceof File);
  const isManualUpload = uploadMode === AllowanceModelUploadMode.MANUAL_UPLOAD;
  const paymentMethod = watch('paymentMethod');
  const allowanceModelClassDefinitions = watch(
    'allowanceModelClassDefinitions'
  );
  const coverageScope = watch('coverageScope');
  const qmeCoverage = watch('qmeCoverage');

  const { isOn: isCoverageScopeEnabled, isReady: isCoverageScopeReady } =
    useEmployerFeatureFlag('coverage_scope', employerId);
  const { isOn: isLynxPaymentsEnabled, isReady: isLynxPaymentsReady } =
    useEmployerFeatureFlag('lynx_payments_enabled', employerId);

  // Wait for both flags before painting — they gate whole sections + isRequired.
  if (!isCoverageScopeReady || !isLynxPaymentsReady) {
    return <Loader />;
  }

  function clearPaymentFields() {
    setValue('initialDrawAmount', undefined);
    setValue('initialDrawDate', undefined);
    setValue('reserveAmount', undefined);
    setValue('thresholdAmount', undefined);
  }

  async function handleAllowanceModelClassDefinitionsInference(file: File) {
    const fileContent = await file.text();
    const result = await extractClassDefinitionsFromCsv(fileContent);
    setValue('allowanceModelClassDefinitions', result, {
      shouldValidate: true,
    });
  }

  function handleClickDeleteAllowanceModel() {
    const options = { shouldValidate: true, shouldDirty: true };
    setValue('allowanceModel', undefined, options);
    setValue('allowanceModelClassDefinitions', null, options);
  }

  function handleUploadModeChange(nextMode: string) {
    setUploadMode(nextMode as AllowanceModelUploadMode);
    if (nextMode === AllowanceModelUploadMode.FROM_QUOTE) {
      handleClickDeleteAllowanceModel();
    }
  }

  function handleCancelAllowanceModelUpdate() {
    const options = { shouldValidate: true, shouldDirty: true };
    setUploadMode(AllowanceModelUploadMode.FROM_QUOTE);
    setValue('allowanceModel', existingAllowanceModel ?? undefined, options);
    setValue('allowanceModelClassDefinitions', null, options);
  }

  return (
    <Grid>
      <Grid.Col span={6}>
        <Controller
          control={control}
          name="effectiveDates"
          render={({ field: { ref: _ref, ...rest } }) => (
            <MonthPickerInput
              {...rest}
              {...getCoverageEndMonthBounds(rest.value)}
              placeholder="Set coverage period dates"
              type="range"
              size="md"
              allowSingleDateInRange
              valueFormat={EN_LOCALE_DATE_FORMAT}
              label={
                <LabelWithTooltip
                  label="Coverage period"
                  tooltipLabel="The coverage period for the plan year"
                  isRequired
                />
              }
            />
          )}
        />
      </Grid.Col>

      <Grid.Col span={6}>
        <Controller
          control={control}
          name="oepDates"
          render={({ field: { ref: _ref, ...rest } }) => (
            <DateRangeInput
              {...rest}
              placeholder="Set open enrollment period dates"
              type="range"
              size="md"
              allowSingleDateInRange
              label={
                <LabelWithTooltip
                  label="Election window"
                  tooltipLabel="The dates when elections will be open for employees during the open enrollment"
                  isRequired
                />
              }
            />
          )}
        />
      </Grid.Col>

      <Grid.Col mt="lg">
        <Group justify="space-between">
          <Title order={3}>Allowance</Title>
          {existingAllowanceModel && !isStoredModelShown && (
            <Button
              variant="secondary"
              size="sm"
              leftSection={<IconX />}
              onClick={handleCancelAllowanceModelUpdate}
            >
              Cancel
            </Button>
          )}
        </Group>
      </Grid.Col>

      {!isStoredModelShown && (
        <Grid.Col>
          <RadioGroup value={uploadMode} onChange={handleUploadModeChange}>
            <Group wrap="nowrap" grow>
              <RadioCard
                label="From quote"
                icon={<IconWand />}
                iconColor="indigo"
                value={AllowanceModelUploadMode.FROM_QUOTE}
                rightContent={
                  <Badge variant="simple" color="indigo">
                    Recommended
                  </Badge>
                }
              />
              <RadioCard
                label="Manual upload"
                icon={<IconUpload />}
                iconColor="indigo"
                value={AllowanceModelUploadMode.MANUAL_UPLOAD}
              />
            </Group>
          </RadioGroup>
          {isManualUpload ? (
            <Text c="var(--color-text-default-secondary)" mt="md" size="sm">
              Upload your allowance model using the provided format; for class
              definitions, see our{' '}
              <Link
                href={CLASS_DEFINITIONS_GUIDE_URL}
                target="_blank"
                variant="secondary"
                rightSection={null}
              >
                guide
              </Link>
            </Text>
          ) : (
            <Alert
              mt="md"
              variant="intro"
              title="Next step: apply from a quote"
            >
              <Stack gap="xxs" align="flex-start">
                <Text>
                  Run a quote in the Quotes tab, then apply the allowance model
                  from the plan design you finalized
                </Text>
                <Link
                  href={APP_ROUTES.OPS_EMPLOYERS_QUOTES({ employerId })}
                  target="_blank"
                  variant="secondary"
                  rightSection={null}
                >
                  Go to Quotes
                </Link>
              </Stack>
            </Alert>
          )}
        </Grid.Col>
      )}

      <Grid.Col>
        {!allowanceModel && isManualUpload && (
          <FileUploader
            control={control as unknown as Control}
            name="allowanceModel"
            label="Allowance Model"
            topRightContent={
              <Link
                href="/files/sample_allowance_model.csv"
                download="sample_allowance_model.csv"
                variant="primary"
              >
                Download sample
              </Link>
            }
            isLoading={false}
            onDrop={(files: File[], onBlur, onChange) => {
              const file = files[0];
              if (file) {
                onChange(file);
                onBlur();
                void handleAllowanceModelClassDefinitionsInference(file);
              }
            }}
            accept={[MIME_TYPES.csv]}
            isMultiple={false}
            isDisabled={!!allowanceModel}
            zoneLabelText="Or drag it here"
            buttonText="Upload Allowance Model"
            isRequired={isAllowanceModelRequired}
          >
            <TableIcon />
          </FileUploader>
        )}
        <AllowanceModelManagement
          allowanceModel={allowanceModel}
          isRequired={isAllowanceModelRequired}
          onClickDelete={handleClickDeleteAllowanceModel}
          sourceLabel={allowanceModelSourceLabel}
        />
        {isStoredModelShown && allowanceModelSourceNote && (
          <Alert mt="md" variant="intro" title="Manual process description">
            <Text style={{ whiteSpace: 'pre-line' }}>
              {allowanceModelSourceNote}
            </Text>
          </Alert>
        )}
        {allowanceModelClassDefinitions && (
          <Alert
            mt="sm"
            variant={
              allowanceModelClassDefinitions.isSuccess ? 'intro' : 'error'
            }
          >
            {allowanceModelClassDefinitions.isSuccess ? (
              <Text fw={700}>
                Review the below extracted class structure and values before
                proceeding:
              </Text>
            ) : (
              <Text fw={700}>
                Your allowance model has errors. Please review the details below
                to fix your file
              </Text>
            )}
            {allowanceModelClassDefinitions.isSuccess ? (
              (() => {
                const { isAllEmployeeOnly, classComponents } =
                  processClassDefinitions(
                    allowanceModelClassDefinitions.classDefinitions
                  );

                if (isAllEmployeeOnly) {
                  return (
                    <Text>
                      Your allowance model contains a single class for all
                      employees
                    </Text>
                  );
                }

                return (
                  <Stack gap="sm">
                    <Text>
                      Your class structure is defined by:{' '}
                      {classComponents.map((component, idx) => (
                        <>
                          {idx > 0 && ', '}
                          <Text key={component.name} span fs="italic">
                            {component.name}
                          </Text>
                        </>
                      ))}
                    </Text>

                    <Text>Detected unique values for each component:</Text>

                    <Stack gap="xs">
                      {classComponents.map((component, _idx) => (
                        <Text>• {component.values}</Text>
                      ))}
                    </Stack>
                  </Stack>
                );
              })()
            ) : (
              <Text style={{ whiteSpace: 'pre-line' }}>
                {allowanceModelClassDefinitions.message}
              </Text>
            )}
          </Alert>
        )}
        {allowanceModel instanceof File && (
          <AllowanceModelSourceFields
            planYearSetupForm={planYearSetupForm}
            employerId={employerId}
          />
        )}
        {isManualUpload && (
          <FormErrorMessage fieldName="allowanceModel" errors={errors} />
        )}
      </Grid.Col>

      {isCoverageScopeEnabled && (
        <>
          <Grid.Col mt="lg">
            <Title order={3}>Coverage scope</Title>
          </Grid.Col>

          <Grid.Col>
            <Controller
              control={control}
              name="coverageScope"
              render={({ field: { value, onChange, ...rest } }) => (
                <RadioGroup
                  {...rest}
                  value={value}
                  onChange={(newValue) => {
                    onChange(newValue);
                    if (newValue === CoverageScope.MEDICAL_ONLY) {
                      setValue('dentalCoverage', false);
                      setValue('visionCoverage', false);
                      setValue('qmeCoverage', false);
                    }
                  }}
                >
                  <Stack gap="md">
                    <Stack gap="xs">
                      <Radio
                        value={CoverageScope.MEDICAL_ONLY}
                        label="Medical premiums only"
                      />
                      <Text
                        c="var(--color-text-default-secondary)"
                        size="sm"
                        ml="xl"
                      >
                        Employees can use their monthly allowance only for
                        medical insurance premiums
                      </Text>
                    </Stack>

                    <Stack gap="xs">
                      <Radio
                        value={CoverageScope.EXPANDED}
                        label="Expanded coverage"
                      />
                      <Text
                        c="var(--color-text-default-secondary)"
                        size="sm"
                        ml="xl"
                      >
                        Unlock usage for other plans and expenses
                      </Text>
                    </Stack>
                  </Stack>
                </RadioGroup>
              )}
            />

            <FormErrorMessage fieldName="coverageScope" errors={errors} />
          </Grid.Col>

          <Grid.Col>
            <Stack gap="md" ml="xl">
              <Controller
                control={control}
                name="dentalCoverage"
                render={({ field: { value, ...rest } }) => (
                  <Switch
                    {...rest}
                    checked={value}
                    disabled={coverageScope === CoverageScope.MEDICAL_ONLY}
                    label={
                      <LabelWithTooltip
                        label="Dental plans"
                        tooltipLabel="Allow employees to utilize their monthly allowance or pay additional pre-tax dollars for Dental coverage."
                      />
                    }
                  />
                )}
              />

              <Controller
                control={control}
                name="visionCoverage"
                render={({ field: { value, ...rest } }) => (
                  <Switch
                    {...rest}
                    checked={value}
                    disabled={coverageScope === CoverageScope.MEDICAL_ONLY}
                    label={
                      <LabelWithTooltip
                        label="Vision plans"
                        tooltipLabel="Allow employees to utilize their monthly allowance or pay additional pre-tax dollars for Vision coverage."
                      />
                    }
                  />
                )}
              />

              <Controller
                control={control}
                name="qmeCoverage"
                render={({ field: { value, ...rest } }) => (
                  <Switch
                    {...rest}
                    checked={value}
                    disabled={coverageScope === CoverageScope.MEDICAL_ONLY}
                    label={
                      <LabelWithTooltip
                        label="Qualified Medical Expenses (QME)"
                        tooltipLabel="Enable reimbursement for eligible out-of-pocket expenses like copays, deductibles, and prescriptions under IRS Section 213(d)."
                      />
                    }
                  />
                )}
              />
            </Stack>
          </Grid.Col>

          {qmeCoverage && (
            <Grid.Col>
              <Alert variant="warning" title="HSA Eligibility Warning">
                <Text size="sm">
                  Enabling reimbursement for general medical expenses classifies
                  this ICHRA/CHOICE as "non-HSA <br />
                  qualified." Employees cannot contribute to a Health Savings
                  Account (HSA) while enrolled in this plan.
                </Text>
                <Box>
                  <Link
                    href=""
                    target="_blank"
                    mt="sm"
                    size="sm"
                    rightSection={null}
                  >
                    Read more about HSA eligibility
                  </Link>
                </Box>
              </Alert>
            </Grid.Col>
          )}
        </>
      )}

      <Grid.Col mt="lg">
        <Title order={3}>Domestic Partner Eligibility</Title>
        <Text c="var(--color-text-default-secondary)" mt="0.25rem" size="sm">
          Set per plan year; When off, employees can add a spouse but not a
          domestic partner. Can't change this after an election with a domestic
          partner is submitted
        </Text>
      </Grid.Col>

      <Controller
        control={control}
        name="isDomesticPartnerAllowed"
        render={({ field: { value, ...rest } }) => (
          <Switch {...rest} checked={value} label="Domestic partners allowed" />
        )}
      />

      {planYearUpdateErrors.length > 0 && (
        <Grid.Col>
          <Alert variant="error">
            <Text fw={600}>
              Error while trying to disable domestic partner eligibility
            </Text>
            <Text>
              The following employees submitted a plan with a domestic partner.
              Remove their domestic partner from the plan to disable the
              domestic partner eligibility. (Use "Reset election" and don't
              preserve the plan selections in cart)
            </Text>
            <List>
              {planYearUpdateErrors.map(({ message, link }) => (
                <List.Item key={message}>
                  <Link href={link} target="_blank" variant="primary">
                    {message}
                  </Link>
                </List.Item>
              ))}
            </List>
          </Alert>
        </Grid.Col>
      )}

      <Grid.Col mt="lg">
        <Title order={3}>Payments</Title>
        <Text c="var(--color-text-default-secondary)" mt="0.25rem" size="sm">
          “Zorro Pay” should be the form of payment in the majority of cases.
          Choose “Self-pay” only if you’ve aligned with Zorro about this
          employer setup.
        </Text>
      </Grid.Col>

      <Grid.Col>
        <Controller
          control={control}
          name="paymentMethod"
          render={({ field: { onChange, ...rest } }) => (
            <RadioGroup
              {...rest}
              onChange={(value) => {
                onChange(value);
                if (value === OEPPaymentMethod.SELF_PAY) clearPaymentFields();
              }}
              label="Payment method"
              required
            >
              <Group wrap="nowrap">
                <RadioCard
                  label={
                    oepPaymentMethodLabelConfig[OEPPaymentMethod.ZORRO_PAY]
                  }
                  icon={<IconZorroPaymentCard />}
                  iconColor="pink"
                  value={OEPPaymentMethod.ZORRO_PAY}
                />
                <RadioCard
                  label={oepPaymentMethodLabelConfig[OEPPaymentMethod.SELF_PAY]}
                  value={OEPPaymentMethod.SELF_PAY}
                  icon={<IconZorroPaymentSelfPay />}
                  iconColor="yellow"
                />
              </Group>
            </RadioGroup>
          )}
        />

        <FormErrorMessage fieldName="paymentMethod" errors={errors} />
      </Grid.Col>

      {paymentMethod === OEPPaymentMethod.ZORRO_PAY && (
        <>
          <Grid.Col span={{ sm: 6 }}>
            <Controller
              control={control}
              name="initialDrawAmount"
              render={({ field: { onChange, ...rest } }) => (
                <CurrencyInput
                  {...rest}
                  onChange={(value) =>
                    onChange(value === '' ? undefined : Number(value))
                  }
                  size="lg"
                  label={
                    <LabelWithTooltip
                      label="Initial draw amount"
                      tooltipLabel="The total amount drawn from the employer's account before enrollment to ensure sufficient funds for all employee payments"
                      isRequired={isFinalizationMode}
                    />
                  }
                />
              )}
            />

            <FormErrorMessage fieldName="initialDrawAmount" errors={errors} />
          </Grid.Col>
          <Grid.Col span={6}>
            <Controller
              control={control}
              name="initialDrawDate"
              render={({ field: { ...rest } }) => (
                <DateInput
                  {...rest}
                  label={
                    <LabelWithTooltip
                      label="Initial draw date"
                      isRequired={isLynxPaymentsEnabled && isFinalizationMode}
                    />
                  }
                  size="lg"
                  minDate={getToday().add(1, 'day').toDate()}
                  excludeDate={(date) => !isBusinessDay(date)}
                />
              )}
            />

            <FormErrorMessage fieldName="initialDrawDate" errors={errors} />
          </Grid.Col>
          <Grid.Col span={{ sm: 6 }}>
            <Controller
              control={control}
              name="reserveAmount"
              render={({ field: { ...rest } }) => (
                <CurrencyInput
                  {...rest}
                  size="lg"
                  label={
                    <LabelWithTooltip
                      label="Reserve amount"
                      tooltipLabel="A reserve kept in the account to ensure enough funds for new employees and enrollment changes during the plan year"
                      isRequired={isLynxPaymentsEnabled && isFinalizationMode}
                    />
                  }
                />
              )}
            />

            <FormErrorMessage fieldName="reserveAmount" errors={errors} />
          </Grid.Col>
          {isLynxPaymentsEnabled && (
            <Grid.Col span={{ sm: 6 }}>
              <Controller
                control={control}
                name="thresholdAmount"
                render={({ field: { ...rest } }) => (
                  <CurrencyInput
                    {...rest}
                    size="lg"
                    label={
                      <LabelWithTooltip
                        label="Threshold amount"
                        tooltipLabel="When reserves fall below this threshold mid-month, an emergency fund pull will be triggered. Must be less than the reserve amount."
                        isRequired={isFinalizationMode}
                      />
                    }
                  />
                )}
              />

              <FormErrorMessage fieldName="thresholdAmount" errors={errors} />
            </Grid.Col>
          )}
        </>
      )}

      <Grid.Col mt="lg">
        <Title order={3}>Agent of record</Title>
      </Grid.Col>

      <Grid.Col span={{ sm: 6 }}>
        <Controller
          control={control}
          name="isAORChanged"
          render={({ field: { value, ...rest } }) => (
            <Checkbox
              {...rest}
              checked={value}
              label="Employer is changing vendors and AOR"
            />
          )}
        />

        <FormErrorMessage errors={errors} fieldName="isAORChanged" />
      </Grid.Col>
    </Grid>
  );
};
